import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faHeart as faHeartStroke } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faHeart as faHeartFilled } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import type { LikeButtonSource } from '@soundxyz/utils/analytics';
import type { IconButtonProps } from '@components/main-components/icon-buttons/IconButton';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { useLikePlaylist } from './useLikePlaylist';
import { useLikeRelease } from './useLikeRelease';

/**
 * This is a base UI component for like button.
 */
export const LikeButton = React.forwardRef<
  HTMLButtonElement,
  {
    liked: boolean;
    onClick: () => void;
    disabled: boolean;
    variant?: IconButtonProps['variant'];
    size?: IconButtonProps['size'];
    iconSize?: IconButtonProps['iconSize'];
    className?: string;
  }
>(function LikeButton(
  { liked, disabled, variant = 'primary', onClick, size = 32, iconSize = 20, className },
  ref,
) {
  return (
    <IconButton
      variant={variant}
      className={clsx('like-icon', className, liked && 'liked')}
      disabled={disabled}
      ref={ref}
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      size={size}
      iconSize={iconSize}
      animate={!disabled}
      icon={
        <FontAwesomeIcon
          icon={liked ? faHeartFilled : faHeartStroke}
          color={liked ? '#EB5CBB' : undefined}
          size="lg"
        />
      }
    />
  );
});

export const LikeReleaseButton = React.forwardRef<
  HTMLButtonElement,
  {
    releaseId?: string;
    // Soon we will remove disabled state for release liking button
    // then we can remove this prop
    disabled: boolean;
    variant?: IconButtonProps['variant'];
    size?: IconButtonProps['size'];
    iconSize?: IconButtonProps['iconSize'];
    source: LikeButtonSource;
    className?: string;
  } & Pick<Parameters<typeof useLikeRelease>[0], 'onUnlikeRelease'>
>(function LikeReleaseButton(props, ref) {
  const { isLiked, onClick } = useLikeRelease({
    id: props.releaseId,
    onUnlikeRelease: props?.onUnlikeRelease,
    source: props.source,
  });

  return (
    <LikeButton
      ref={ref}
      onClick={onClick}
      liked={isLiked}
      disabled={props.disabled}
      variant={props.variant}
      className={props.className}
    />
  );
});

export const LikePlaylistButton = React.forwardRef<
  HTMLButtonElement,
  {
    playlistId: string;
    disabled: boolean;
    variant?: IconButtonProps['variant'];
    source: LikeButtonSource;
  }
>(function LikePlaylistButton(props, ref) {
  const { isLiked, onClick } = useLikePlaylist({ id: props.playlistId, source: props.source });
  // there is no disabled state for playlist liking button
  return (
    <LikeButton
      ref={ref}
      onClick={onClick}
      liked={isLiked}
      disabled={false}
      variant={props.variant}
    />
  );
});
